import { useState, useRef, useEffect, useCallback } from "react";
import { api } from '../api';
import { toast, useEscapeClose } from '../utils';
import { beregn_deadline_info } from '../components/KlientOversigt';
import { useRecentlyViewed } from './useRecentlyViewed';
import { RAEKKEFOELGE } from '../utils/sagHelpers';

// Polling-interval: 10 minutter (René: "den behøver kun polle hver 10 minut")
const POLL_INTERVAL_MS = 600000;
// "Nye hændelser"-widgeten skal "komme af sig selv" (René 4/10) -- eget,
// hurtigere interval. Endpointet er billigt (ét simpelt SELECT), og
// hold-størrelsen er lille, så 60 sek. er ikke en belastning.
const NYE_HAENDELSER_POLL_MS = 60000;

export function useDashboard() {
  const [visning, setVisning] = useState("menu");
  const [, setNotifikationer] = useState([]);
  const [, setAfvisteNotif] = useState({});
  const [aktiv_filter, setAktivFilter] = useState(null);
  const [sag_visning, setSagVisning] = useState(() => localStorage.getItem("sv_sag_visning") || "liste");
  const [import_aktiv, setImportAktiv] = useState(null);
  const [server_version, setServerVersion] = useState('...');
  const initialGitCommit = useRef(null);
  const [nyVersionBanner, setNyVersionBanner] = useState(false);

  const [sag_sortering, setSagSortering] = useState("deadline");
  const { recentlyViewed, trackViewed } = useRecentlyViewed();
  const [aktivitet_data, setAktivitetData] = useState([]);
  const [mine_sager, setMineSager] = useState([]);
  const [total_sager, setTotalSager] = useState(0);
  const [åbn_sag_id, setÅbnSagId] = useState(null);
  const [maal_haendelse_id, setMaalHaendelseId] = useState(null);
  const [nye_haendelser_liste, setNyeHaendelserListe] = useState([]);
  const [dashboard_deadline_dialog, setDashboardDeadlineDialog] = useState(null);
  useEscapeClose(!!dashboard_deadline_dialog, () => setDashboardDeadlineDialog(null));
  const [bruger, setBruger] = useState(() => {
    try {
      // SEC-SESSION: sessionStorage forsvinder ved browser-close (vs. localStorage
      // der overlever reboot). Token er nu httpOnly cookie — kun bruger-objekt
      // gemmes i sessionStorage til UI-state-restore.
      const gemt = sessionStorage.getItem("sv_bruger");
      return gemt ? JSON.parse(gemt) : null;
    } catch { return null; }
  });

  // Ryd udløbne udsættelser hvert minut
  useEffect(() => {
    const ryd = setInterval(() => {
      const nu = Date.now();
      setAfvisteNotif(p => {
        const opdateret = { ...p };
        let ændret = false;
        Object.keys(opdateret).forEach(id => {
          if (opdateret[id] !== Infinity && opdateret[id] < nu) {
            delete opdateret[id];
            ændret = true;
          }
        });
        return ændret ? opdateret : p;
      });
    }, 60000);
    return () => clearInterval(ryd);
  }, []);

  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => {
      if (d?.version) setServerVersion(d.version);
      if (d?.git_commit) initialGitCommit.current = d.git_commit;
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!initialGitCommit.current) return;
      fetch('/api/health').then(r => r.json()).then(d => {
        if (d?.git_commit && d.git_commit !== initialGitCommit.current) {
          setNyVersionBanner(true);
        }
      }).catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // PWA: service workeren (src/index.js) dispatcher dette event når en ny
  // version er installeret og klar. Genbruger samme synlige banner som
  // /api/health-pollingen ovenfor, så der kun findes ÉT "ny version"-UI.
  useEffect(() => {
    const paaOpdatering = () => setNyVersionBanner(true);
    window.addEventListener('sv:sw-update-available', paaOpdatering);
    return () => window.removeEventListener('sv:sw-update-available', paaOpdatering);
  }, []);

  const hent_mine_sager = useCallback(() => {
    api("sager").then(data => {
      if (data && data.sager) {
        setMineSager(data.sager.filter(s => s.status !== "lukket").map(s => ({
          id: String(s.id),
          sagsnummer: s.sagsnummer || `SV-${String(s.id).padStart(4,"0")}`,
          klient: s.klient_navn || "Ukendt",
          type: s.type || "—",
          status: s.status || "aktiv",
          fase: s.fase || "—",
          risiko: s.risiko_score || 0,
          styrke_farve: (s.risiko_score||0) >= 70 ? "#69db7c" : (s.risiko_score||0) >= 40 ? "#ffd43b" : "#ff6b6b",
          deadline: s.næste_deadline || s.tidligste_haendelse_deadline || null,
          deadline_titel: s.næste_deadline_titel || "",
          deadline_txt: beregn_deadline_info(s.næste_deadline || s.tidligste_haendelse_deadline).txt,
          deadline_farve: beregn_deadline_info(s.næste_deadline || s.tidligste_haendelse_deadline).farve,
          primær: s.primær_navn || "—",
          primær_id: s.primær_sagsbehandler_id,
          sekundær: s.sekundær_navn || null,
          sekundær_id: s.sekundær_sagsbehandler_id,
          haendelser: s.haendelse_antal || 0,
          har_nye_haendelser: s.har_nye_haendelser === true,
          opgaver: s.åbne_opgaver || 0,
          klient_maal: s.klient_maal || "",
          aftaler: s.aftaler || "",
        })));
      }
    }).catch(err => { console.error(err); toast("En handling fejlede — prøv igen", "fejl"); });
    // Hent total-tal for hele kontoret
    api("sager/total").then(data => { if (data?.antal) setTotalSager(data.antal); }).catch(() => {});
  }, []);

  // Individuelle usete hændelser til "Nye hændelser"-widgeten (René: skal
  // vise HVAD hændelsen er, ikke kun hvilken sag — og klikke direkte hen
  // til den specifikke hændelse, ikke blot sagens overblik-fane).
  const hent_nye_haendelser = useCallback(() => {
    api("haendelser/mine/nye").then(data => {
      if (data?.haendelser) setNyeHaendelserListe(data.haendelser);
    }).catch(() => {});
  }, []);

  // René: "den skal jo komme af sig selv og forsvinde af sig selv når den
  // er set" -- kaldes optimistisk fra KlientOversigt lige efter en hændelse
  // markeres set, så widgeten opdateres med det samme uden at vente på
  // næste poll eller en manuel genindlæsning.
  const fjern_nye_haendelse = useCallback((haendelse_id) => {
    setNyeHaendelserListe(p => p.filter(h => h.id !== haendelse_id));
  }, []);

  // Import kører på App-niveau — overlever navigation
  const kør_import = useCallback(async (formData, klient_label) => {
    setImportAktiv({ status_tekst:"Uploader filer til OneDrive...", progress:20, fejl:null, klient:klient_label });
    try {
      const res = await fetch((window.SV_API || "/api/") + "import/importer", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      setImportAktiv(prev => ({ ...prev, progress:80, status_tekst:"Opretter hændelser og mapper..." }));
      if (!res.ok) throw new Error("Import fejlede: " + res.status);
      const data = await res.json();
      if (data.fejl) throw new Error(data.fejl);
      setImportAktiv({ progress:100, status_tekst:"Import fuldført! " + data.uploadede_filer + "/" + data.total_filer + " filer", fejl:null, klient:klient_label });
      toast("Sag importeret: " + data.sagsnummer, "succes");
      hent_mine_sager();
      setTimeout(() => setImportAktiv(null), 4000);
    } catch (e) {
      console.error('[Import] fejl:', e);
      setImportAktiv({ progress:0, status_tekst:"Import fejlede", fejl:"Import fejlede — prøv igen", klient:klient_label });
      toast("Import fejlede — prøv igen", "fejl");
    }
  }, [hent_mine_sager]);

  const hent_notifikationer = useCallback(() => {
    api("notifikationer").then(data => {
      if (data && data.notifikationer) {
        const nu = Date.now();
        // Filtrer afviste og ikke-udløbne udsatte notifikationer fra
        setAfvisteNotif(prev => {
          const opdateret = { ...prev };
          Object.keys(opdateret).forEach(id => {
            if (opdateret[id] !== Infinity && opdateret[id] < nu) {
              delete opdateret[id];
            }
          });
          setNotifikationer(data.notifikationer.filter(n => {
            const udløber = opdateret[n.id];
            if (udløber === undefined) return true;  // ikke afvist
            if (udløber === Infinity) return false;  // permanent afvist
            return udløber < nu;                     // udsættelse udløbet
          }));
          return opdateret;
        });
      }
    }).catch(err => { console.error(err); toast("En handling fejlede — prøv igen", "fejl"); });
  }, []);

  useEffect(() => {
    if (!bruger) return;
    hent_mine_sager();
    hent_notifikationer();
    hent_nye_haendelser();
    // Hent aktivitetsdata for graf (14 dage) -- aggregeret server-side
    // (se routes/haendelser.js GET /aktivitet/graf): tidligere blev kun de
    // 50 seneste hændelser globalt talt op client-side, hvilket ved normal
    // aktivitet nemt udfyldtes helt af de sidste 1-2 dage og viste 0 for
    // resten af perioden.
    (async () => {
      const dage_data = [];
      for (let i = 13; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const dato = d.toISOString().split("T")[0];
        const label = d.toLocaleDateString("da-DK", { day:"numeric", month:"short" });
        dage_data.push({ dato, label, antal: 0 });
      }
      const res = await api("haendelser/aktivitet/graf?dage=14");
      if (res?.graf) {
        res.graf.forEach(r => {
          const r_dato = new Date(r.dato).toISOString().split("T")[0];
          const match = dage_data.find(d => d.dato === r_dato);
          if (match) match.antal = r.antal;
        });
      }
      setAktivitetData(dage_data);
    })();
    const interval = setInterval(() => {
      hent_mine_sager();
      hent_notifikationer();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [bruger, hent_mine_sager, hent_notifikationer, hent_nye_haendelser]);

  // "Nye hændelser" skal komme og forsvinde "af sig selv" (René 4/10) --
  // eget hurtigere poll-interval end resten af dashboardet, plus et
  // øjeblikkeligt genhent hver gang brugeren lander på Overblik (visning
  // skifter til "menu"), så fanen altid er frisk når man kigger på den,
  // ikke kun ved login eller hvert 10. minut.
  useEffect(() => {
    if (!bruger) return;
    const interval = setInterval(hent_nye_haendelser, NYE_HAENDELSER_POLL_MS);
    return () => clearInterval(interval);
  }, [bruger, hent_nye_haendelser]);

  useEffect(() => {
    if (!bruger || visning !== "menu") return;
    hent_nye_haendelser();
  }, [visning, bruger, hent_nye_haendelser]);

  // Museknap tilbage (knap 3) + keyboard genveje
  useEffect(() => {
    const håndter_mus = (e) => {
      if (e.button === 3) { e.preventDefault(); setVisning("menu"); }
      if (e.button === 4) { e.preventDefault();
        setVisning(p => {
          const idx = RAEKKEFOELGE.indexOf(p);
          return idx < RAEKKEFOELGE.length-1 ? RAEKKEFOELGE[idx+1] : p;
        });
      }
    };
    const håndter_keys = (e) => {
      if (e.key === "Escape") { setVisning("menu"); }
      if (e.altKey && e.key === "ArrowLeft") { setVisning("menu"); }
    };
    window.addEventListener("mousedown", håndter_mus);
    window.addEventListener("keydown", håndter_keys);
    return () => {
      window.removeEventListener("mousedown", håndter_mus);
      window.removeEventListener("keydown", håndter_keys);
    };
  }, []);

  const GYLDIGE_VISNINGER = ["menu","dashboard","klienter","ny-klient","import","import-onedrive","søg","infoskærm","indstillinger","min-profil","hjaelp","os","teamnoter","filtjek"];
  const SIDE_TITLER = { menu:"Hjem", klienter:"Sager", "ny-klient":"Ny sag", søg:"Søg", dashboard:"Dashboard", "min-profil":"Min profil", indstillinger:"Indstillinger", import:"Import", os:"OS Dashboard", teamnoter:"Team-noter", hjaelp:"Hjælp", infoskærm:"Infoskærm", filtjek:"Filtjek" };
  const sæt_titel = (ny_visning) => { document.title = "SkilsmisseVejen — " + (SIDE_TITLER[ny_visning] || ny_visning); };

  const naviger = (ny_visning) => {
    // A11: Deep-linking via path
    const sti = ny_visning === "menu" ? "/" : "/" + ny_visning;
    window.history.pushState({ visning: ny_visning }, "", sti);
    setVisning(ny_visning);
    sæt_titel(ny_visning);
  };

  // A11: Læs visning fra URL ved første load
  useEffect(() => {
    const sti = window.location.pathname.replace(/^\//, "") || "menu";
    if (GYLDIGE_VISNINGER.includes(sti)) {
      setVisning(sti);
      sæt_titel(sti);
    }
  }, []);

  // Browser tilbage/frem-knapper: naviger() pushede nye URL'er til historikken,
  // men uden en popstate-lytter opdaterede appen aldrig sin egen visning når
  // browseren selv skiftede side i historikken -- brugeren oplevede derfor at
  // "tilbage" ikke gjorde noget, indtil man ramte den allerførste side-indlæsning.
  // Lytteren her genskaber normal browser-historik-opførsel ét skridt ad gangen.
  useEffect(() => {
    const håndter_popstate = (e) => {
      const ny = e.state?.visning || window.location.pathname.replace(/^\//, "") || "menu";
      if (GYLDIGE_VISNINGER.includes(ny)) {
        setVisning(ny);
        sæt_titel(ny);
      }
    };
    window.addEventListener("popstate", håndter_popstate);
    return () => window.removeEventListener("popstate", håndter_popstate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #53: Track nyligt åbnede sager
  useEffect(() => {
    if (åbn_sag_id) {
      const sag = mine_sager.find(s => s.id === åbn_sag_id);
      if (sag) trackViewed(sag);
    }
  }, [åbn_sag_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const håndter_notif = (id, handling, sag_ref) => {
    setNotifikationer(p => p.filter(n => n.id !== id));
    setAfvisteNotif(p => ({ ...p, [id]: Infinity }));
    if (id.startsWith('db-')) api('notifikationer/' + id.slice(3) + '/laest', 'PATCH').catch(() => {});
    if (handling === "aaben_sag") {
      if (sag_ref) {
        const sag = mine_sager.find(s => s.id === String(sag_ref) || s.sagsnummer === String(sag_ref));
        if (sag) setÅbnSagId(sag.id);
      }
      naviger("klienter");
    }
  };
  // eslint-disable-next-line no-unused-vars
  const afvis_notif = (id) => {
    setNotifikationer(p => p.filter(n => n.id !== id));
    setAfvisteNotif(p => ({ ...p, [id]: Infinity }));
    if (id.startsWith('db-')) api('notifikationer/' + id.slice(3) + '/laest', 'PATCH').catch(() => {});
  };
  // eslint-disable-next-line no-unused-vars
  const udsæt_notif = (id, minutter) => {
    const udløber = Date.now() + minutter * 60 * 1000;
    setNotifikationer(p => p.filter(n => n.id !== id));
    setAfvisteNotif(p => ({ ...p, [id]: udløber }));
  };

  const log_ud = useCallback((server_kald = true) => {
    // SEC-SESSION: Kald server-logout så token blacklistes. Fire-and-forget
    // fordi vi vil logge ud lokalt uanset om kaldet lykkes.
    if (server_kald) {
      try { api("auth/logout", "POST"); } catch (_) {}
    }
    sessionStorage.removeItem("sv_token_exp");
    sessionStorage.removeItem("sv_bruger");
    // Signal til andre faner: de skal også logge ud
    try { localStorage.setItem("sv_logout_signal", String(Date.now())); } catch (_) {}
    setBruger(null);
    setVisning("menu");
    setNotifikationer([]);
    setMineSager([]);
    setAktivFilter(null);
  }, []);

  // SEC-SESSION: Lyt efter logout-signal fra andre faner.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "sv_logout_signal" && bruger) {
        sessionStorage.removeItem("sv_token_exp");
        sessionStorage.removeItem("sv_bruger");
        setBruger(null);
        setVisning("menu");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [bruger]);

  // SEC-SESSION: Idle-timeout — automatisk logout efter 20 min uden aktivitet.
  // Undtaget for infoskærm-rolle (kiosk-brug).
  useEffect(() => {
    if (!bruger) return;
    const er_infoskaerm = bruger.rolle === "infoskærm" || bruger.rolle === "infoskaerm";
    if (er_infoskaerm) return;

    const IDLE_MS = 20 * 60 * 1000; // 20 minutter
    let timer = null;
    const nulstil = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (window.__sv_toast) window.__sv_toast("Du er logget ud pga. inaktivitet", "advarsel");
        log_ud(true);
      }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(ev => window.addEventListener(ev, nulstil, { passive: true }));
    nulstil();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach(ev => window.removeEventListener(ev, nulstil));
    };
  }, [bruger, log_ud]);

  // SEC-SESSION: Auto-fornyelse — fornyer token stille mens brugeren er aktiv.
  // Undgår uventet logout under lange arbejdssessioner (JWT er 2 timer fast).
  useEffect(() => {
    if (!bruger) return;
    const er_infoskaerm = bruger.rolle === "infoskærm" || bruger.rolle === "infoskaerm";
    if (er_infoskaerm) return;

    const FORNY_INDEN_MS = 15 * 60 * 1000; // Forny når < 15 min tilbage
    let kørende = false; // Guard mod samtidige kald

    const tjek = async () => {
      if (kørende) return;
      try {
        const exp_ms = sessionStorage.getItem("sv_token_exp");
        if (!exp_ms) return;
        const tilbage_ms = parseInt(exp_ms, 10) - Date.now();
        if (tilbage_ms <= 0 || tilbage_ms >= FORNY_INDEN_MS) return;
        kørende = true;
        const r = await api("auth/refresh", "POST");
        if (r?.exp) {
          sessionStorage.setItem("sv_token_exp", String(r.exp * 1000));
          if (r.bruger) sessionStorage.setItem("sv_bruger", JSON.stringify(r.bruger));
        }
      } catch (_) {
      } finally {
        kørende = false;
      }
    };

    const interval = setInterval(tjek, 60 * 1000); // Tjek hvert minut
    tjek(); // Tjek straks ved mount
    return () => clearInterval(interval);
  }, [bruger]);

  return {
    visning, setVisning, naviger,
    aktiv_filter, setAktivFilter,
    sag_visning, setSagVisning,
    import_aktiv, setImportAktiv,
    server_version,
    nyVersionBanner, setNyVersionBanner,
    sag_sortering, setSagSortering,
    recentlyViewed,
    aktivitet_data,
    mine_sager, setMineSager,
    total_sager,
    åbn_sag_id, setÅbnSagId,
    maal_haendelse_id, setMaalHaendelseId,
    nye_haendelser_liste, hent_nye_haendelser, fjern_nye_haendelse,
    dashboard_deadline_dialog, setDashboardDeadlineDialog,
    bruger, setBruger,
    hent_mine_sager,
    kør_import,
    log_ud,
    håndter_notif,
    afvis_notif,
    udsæt_notif,
  };
}
